// src/pages/LandlordDocuments.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_LANDLORD_DOCUMENTS, UPLOAD_DOCUMENT, DELETE_DOCUMENT, UPDATE_BANKING_DETAILS } from '../graphql/landlords';

const LandlordDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // States for banking details form
  const [bankingDetails, setBankingDetails] = useState({
    bankName: '',
    accountNumber: '',
    accountType: '',
    branchCode: '',
    accountHolder: ''
  });
  
  // States for document upload
  const [uploadType, setUploadType] = useState('contract');
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  
  // States for modal visibility
  const [showBankingModal, setShowBankingModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  
  // Document type labels
  const documentTypeLabels = {
    contract: 'Contract',
    taxDocuments: 'Tax Documents',
    propertyTitle: 'Property Title',
    identityDocument: 'Identity Document',
    other: 'Other Document'
  };
  
  // GraphQL Queries and Mutations
  const { loading, error, data, refetch } = useQuery(GET_LANDLORD_DOCUMENTS, {
    variables: { id },
    fetchPolicy: 'network-only'
  });
  
  const [uploadDocument, { loading: uploading }] = useMutation(UPLOAD_DOCUMENT, {
    onCompleted: () => {
      refetch();
      setShowUploadModal(false);
      setFile(null);
      setUploadType('contract');
    },
    onError: (error) => {
      setUploadError(error.message);
    }
  });
  
  const [deleteDocument] = useMutation(DELETE_DOCUMENT, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => {
      alert(`Error deleting document: ${error.message}`);
    }
  });
  
  const [updateBankingDetails, { loading: updatingBanking }] = useMutation(UPDATE_BANKING_DETAILS, {
    onCompleted: () => {
      refetch();
      setShowBankingModal(false);
    },
    onError: (error) => {
      alert(`Error updating banking details: ${error.message}`);
    }
  });
  
  // Update banking details state when data is loaded
  useEffect(() => {
    if (data?.landlord?.bankingDetailsInfo) {
      setBankingDetails({
        bankName: data.landlord.bankingDetailsInfo.bankName || '',
        accountNumber: data.landlord.bankingDetailsInfo.accountNumber || '',
        accountType: data.landlord.bankingDetailsInfo.accountType || '',
        branchCode: data.landlord.bankingDetailsInfo.branchCode || '',
        accountHolder: data.landlord.bankingDetailsInfo.accountHolder || ''
      });
    }
  }, [data]);
  
  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      console.log('File selected:', {
        name: selectedFile.name,
        type: selectedFile.type,
        size: selectedFile.size
      });
      
      // Validate file type
      const fileType = selectedFile.type;
      if (fileType === 'application/pdf' || fileType === 'image/jpeg' || fileType === 'image/jpg') {
        setFile(selectedFile);
        setUploadError('');
      } else {
        setFile(null);
        setUploadError('Only PDF and JPG files are allowed');
      }
    }
  };
  
  // Handle document upload
  const handleUpload = async (e) => { 
    e.preventDefault();
    
    if (!file) {
      setUploadError('Please select a file to upload');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('landlordId', id);
      formData.append('type', uploadType);
      
      console.log('Uploading via REST:', file.name);
      
      const response = await fetch('http://localhost:4000/api/upload-document', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }
      
      const result = await response.json();
      console.log('Upload successful:', result);
      
      // Refresh the documents list
      refetch();
      setShowUploadModal(false);
      setFile(null);
      setUploadType('contract');
    } catch (error) {
      console.error('Upload error details:', error);
      setUploadError(error.message || 'Upload failed');
    }
  };
  
  // Handle banking details update
  const handleBankingDetailsSubmit = (e) => {
    e.preventDefault();
    
    console.log('Submitting banking details:', bankingDetails);
    
    updateBankingDetails({
      variables: {
        landlordId: id,
        input: bankingDetails
      },
      onCompleted: (data) => {
        console.log('Banking details updated successfully:', data);
        refetch();
        setShowBankingModal(false);
      },
      onError: (error) => {
        console.error('Error updating banking details:', error);
        alert(`Error updating banking details: ${error.message}`);
      }
    });
  };
  
  // Handle banking details form changes
  const handleBankingDetailsChange = (e) => {
    const { name, value } = e.target;
    setBankingDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle document deletion
  const handleDeleteDocument = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      deleteDocument({
        variables: { id: documentId }
      });
    }
  };
  
  // View document
  const handleViewDocument = (document) => {
    setCurrentDocument(document);
    setShowViewModal(true);
  };
  
  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  const { landlord } = data;
  const documents = landlord.documents || [];
  
  // Group documents by type
  const documentsByType = {};
  documents.forEach(doc => {
    if (!documentsByType[doc.type]) {
      documentsByType[doc.type] = [];
    }
    documentsByType[doc.type].push(doc);
  });

  
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Documents for {landlord.firstName} {landlord.lastName}
        </h1>
        <Link to={`/landlords/${id}`} className="flex items-center text-blue-600 hover:text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Landlord Details
        </Link>
      </div>
      
      {/* Action Buttons */}
      <div className="mb-6 flex flex-wrap gap-4">
        <button
          onClick={() => setShowBankingModal(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          {landlord.bankingDetails ? 'Update Banking Details' : 'Add Banking Details'}
        </button>
        
        <button
          onClick={() => setShowUploadModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Upload Document
        </button>
      </div>
      
      {/* Banking Details Section */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Banking Details</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Landlord's banking information for payments
            </p>
          </div>
          <div className="border-t border-gray-200">
            {landlord.bankingDetailsInfo ? (
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Bank Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {landlord.bankingDetailsInfo.bankName}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Account Holder</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {landlord.bankingDetailsInfo.accountHolder}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Account Number</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {landlord.bankingDetailsInfo.accountNumber}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Account Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {landlord.bankingDetailsInfo.accountType}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Branch Code</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {landlord.bankingDetailsInfo.branchCode}
                  </dd>
                </div>
              </dl>
            ) : (
              <div className="px-4 py-5 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No banking details have been added yet.</p>
                <div className="mt-4">
                  <button
                    onClick={() => setShowBankingModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Banking Details
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      
      {/* Documents Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Uploaded Documents</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            All documentation related to this landlord
          </p>
        </div>
        <div className="border-t border-gray-200">
          {documents.length > 0 ? (
            <div>
              {Object.keys(documentsByType).map(type => (
                <div key={type} className="mb-6">
                  <h4 className="px-4 py-2 bg-gray-50 text-md font-medium text-gray-700">
                    {documentTypeLabels[type] || type}
                  </h4>
                  <ul className="divide-y divide-gray-200">
                    {documentsByType[type].map(document => (
                      <li key={document.id} className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                        <div>
                          <p className="text-sm font-medium text-blue-600">{document.name}</p>
                          
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewDocument(document)}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View
                          </button>
                          <a
                            href={document.url}
                            download={document.name}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(document.id)}
                            className="inline-flex items-center px-3 py-1 border border-red-300 text-sm leading-5 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-5 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No documents have been uploaded yet.</p>
              <div className="mt-4">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Document
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Banking Details Modal */}
      {showBankingModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowBankingModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">{landlord.bankingDetails ? 'Update Banking Details' : 'Add Banking Details'}</h3>
                  <div className="mt-2">
                    <form onSubmit={handleBankingDetailsSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">Bank Name</label>
                        <input
                          type="text"
                          name="bankName"
                          id="bankName"
                          value={bankingDetails.bankName}
                          onChange={handleBankingDetailsChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="accountHolder" className="block text-sm font-medium text-gray-700">Account Holder</label>
                        <input
                          type="text"
                          name="accountHolder"
                          id="accountHolder"
                          value={bankingDetails.accountHolder}
                          onChange={handleBankingDetailsChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">Account Number</label>
                        <input
                          type="text"
                          name="accountNumber"
                          id="accountNumber"
                          value={bankingDetails.accountNumber}
                          onChange={handleBankingDetailsChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="accountType" className="block text-sm font-medium text-gray-700">Account Type</label>
                        <select
                          name="accountType"
                          id="accountType"
                          value={bankingDetails.accountType}
                          onChange={handleBankingDetailsChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">Select Account Type</option>
                          <option value="Savings">Savings</option>
                          <option value="Cheque">Cheque</option>
                          <option value="Current">Current</option>
                          <option value="Credit Card">Credit Card</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="branchCode" className="block text-sm font-medium text-gray-700">Branch Code</label>
                        <input
                          type="text"
                          name="branchCode"
                          id="branchCode"
                          value={bankingDetails.branchCode}
                          onChange={handleBankingDetailsChange}
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={updatingBanking}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                          {updatingBanking ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowBankingModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowUploadModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Upload Document</h3>
                  <div className="mt-2">
                    <form onSubmit={handleUpload} className="space-y-4">
                      <div>
                        <label htmlFor="documentType" className="block text-sm font-medium text-gray-700">Document Type</label>
                        <select
                          id="documentType"
                          value={uploadType}
                          onChange={(e) => setUploadType(e.target.value)}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="contract">Contract</option>
                          <option value="taxDocuments">Tax Documents</option>
                          <option value="propertyTitle">Property Title</option>
                          <option value="identityDocument">Identity Document</option>
                          <option value="other">Other Document</option>
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="file" className="block text-sm font-medium text-gray-700">Select File (PDF or JPG only)</label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                          <div className="space-y-1 text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div className="flex text-sm text-gray-600">
                              <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                <span>Upload a file</span>
                                <input 
                                  id="file-upload" 
                                  name="file-upload" 
                                  type="file" 
                                  accept=".pdf,.jpg,.jpeg" 
                                  className="sr-only"
                                  onChange={handleFileChange}
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PDF or JPG up to 10MB</p>
                          </div>
                        </div>
                        {file && (
                          <p className="mt-2 text-sm text-green-600">
                            Selected file: {file.name}
                          </p>
                        )}
                        {uploadError && (
                          <p className="mt-2 text-sm text-red-600">
                            {uploadError}
                          </p>
                        )}
                      </div>
                      
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={uploading || !file}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                          {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowUploadModal(false)}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {showViewModal && currentDocument && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowViewModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">{currentDocument.name}</h3>
                    <button
                      onClick={() => setShowViewModal(false)}
                      className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="mt-4 bg-gray-50 p-4 h-96 flex items-center justify-center overflow-auto">
                    {currentDocument.type === 'application/pdf' ? (
                      <iframe
                        src={currentDocument.url}
                        title={currentDocument.name}
                        className="w-full h-full"
                      ></iframe>
                    ) : (
                      <img
                        src={currentDocument.url}
                        alt={currentDocument.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    )}
                  </div>
                  <div className="mt-4 flex justify-end space-x-3">
                    <a
                      href={currentDocument.url}
                      download={currentDocument.name}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </a>
                    <button
                      onClick={() => {
                        handleDeleteDocument(currentDocument.id);
                        setShowViewModal(false);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandlordDocuments;